import { useState } from 'react'

import { useDebouncedCallback } from 'use-debounce'
import { useSelector } from 'react-redux'

import { useAppDispatch, useAppSelector } from 'src/Hooks/redux'
import { getAudienceTiktok } from 'src/Redux/schedule-process/audienceTiktokSlice/api-actions'
import { DEBOUNCE_TIMER_300 } from 'src/Constants/constants'
import {
  getIsTiktokAudienceLoading,
  getIsTiktokAudienceRequestNeeded,
  getTiktokPrice,
} from 'src/Redux/schedule-process/audienceTiktokSlice/selectors'
import { useScheduleEvents } from 'src/Hooks/useScheduleEvents'
import {
  CampaignPlatformTypeEnum,
  TikTokCampaignPricingModelEnum,
} from 'src/Types/campaigns'
import { getScheduledTikTokCampaign } from 'src/Redux/schedule-process/tiktokCampaign/selectors'
import { useGetTikTokCampaignBudgetRangeQuery } from 'src/Redux/artistPaymentSettingsApi'
import { useGetCurrencyInfoQuery } from 'src/Redux/userExchangeRateApi'

import { useSyncTikTokBudgetValues } from './useSyncTikTokBudgetValues'

const validateValue = (value: number, min: number, max: number): number => {
  if (value > max) {
    return max
  }

  if (value < min) {
    return min
  }

  return value
}

interface UseTiktokPrice {
  valueInput: number
  loading: boolean
  valueSlider: number
  setValueInput: (value: number) => void
  setValueSlider: (value: number) => void
  savePrice: (value: number) => void
  rate: number
  currencySymbol: string
  pricingModel?: TikTokCampaignPricingModelEnum | null
}

export const useTiktokPrice = (): UseTiktokPrice => {
  const scheduledCampaign = useSelector(getScheduledTikTokCampaign)
  const { pricingModel } = scheduledCampaign

  const { data: budgetRange } = useGetTikTokCampaignBudgetRangeQuery(
    pricingModel ?? null,
  )

  const min = Number(budgetRange?.campaignMinBudget)
  const max = Number(budgetRange?.campaignMaxBudget)

  const dispatch = useAppDispatch()

  const { data: currentCurrency } = useGetCurrencyInfoQuery()
  const currencySymbol = currentCurrency?.currencySymbol ?? '$'
  const currencyRate = currentCurrency?.currencyRate ?? 1

  const price = useAppSelector(getTiktokPrice)
  const loading = useAppSelector(getIsTiktokAudienceLoading)
  const isAudienceRequestNeeded = useAppSelector(
    getIsTiktokAudienceRequestNeeded,
  )

  const { handleBudgetEvents } = useScheduleEvents()

  const [valueInput, setValueInput] = useState(0)
  const [valueSlider, setValueSlider] = useState(0)

  const savePrice = useDebouncedCallback((value: number) => {
    if (!pricingModel) {
      return
    }

    void dispatch(
      getAudienceTiktok({
        amount: validateValue(value, min, max),
        pricingModel,
      }),
    )
      .unwrap()
      .then((data) => {
        handleBudgetEvents({
          platformName: CampaignPlatformTypeEnum.TIKTOK,
          resultPrice: data.price,
        })

        setValueInput(Math.round(data.price * currencyRate))
        setValueSlider(value)

        return data
      })
  }, DEBOUNCE_TIMER_300)

  useSyncTikTokBudgetValues({
    min,
    max,
    price,
    currencyRate,
    isAudienceRequestNeeded,
    pricingModel,
    setValueInput,
    setValueSlider,
    savePrice,
  })

  return {
    pricingModel,
    currencySymbol,
    valueInput,
    valueSlider,
    setValueInput,
    setValueSlider,
    savePrice,
    loading,
    rate: currencyRate,
  }
}
